import axios from 'axios'
import { i18n } from '@/plugins/util'
import type { WalletBankTransferResult, WalletSummary } from '@/components/appDriver/wallet/types'
import { formatLakAmountValue, maskBankAccountNumber } from '@/components/appDriver/wallet/utils'

const DEFAULT_MAX_AMOUNT_INPUT = 99999999
const DRIVER_LOCATION_OPTIONS: PositionOptions = {
  enableHighAccuracy: false,
  maximumAge: 60000,
  timeout: 3000,
}
const DRIVER_LOCATION_PERMISSION_GRANTED_KEY = 'LOCA_WALLET_LOCATION_PERMISSION_GRANTED'

export function getWalletRequestErrorMessage(
  error: unknown,
  currentLang: string,
  fallbackMessage = i18n(currentLang, ['Please try again', 'ກະລຸນາລອງໃໝ່']),
): string {
  if (axios.isAxiosError(error)) {
    if (error.response) {
      return error.response.data?.message ?? fallbackMessage
    }

    if (error.request) {
      return i18n(currentLang, ['Cannot connect to server, please try again', 'ບໍ່ສາມາດເຊື່ອມຕໍ່ລະບົບ ກະລຸນາລອງໃໝ່'])
    }
  }

  if (error instanceof Error && error.message) {
    return error.message
  }

  return fallbackMessage
}

export function parseWalletAmountInput(value: string, maxDigits: number): number {
  const numericValue = Number(value.replace(/\D/g, '').slice(0, maxDigits))

  return Number.isFinite(numericValue) ? numericValue : 0
}

export function formatWalletAmountInput(value: number, maxInputAmount = DEFAULT_MAX_AMOUNT_INPUT): string {
  const normalizedValue = Math.min(Math.trunc(value), maxInputAmount)

  if (normalizedValue <= 0) {
    return ''
  }

  return normalizedValue.toLocaleString('en-US')
}

export function shouldBlockWalletAmountKeydown(event: KeyboardEvent, maxDigits: number): boolean {
  const allowedControlKeys = [
    'Backspace',
    'Delete',
    'ArrowLeft',
    'ArrowRight',
    'ArrowUp',
    'ArrowDown',
    'Home',
    'End',
    'Tab',
  ]

  if (allowedControlKeys.includes(event.key) || event.metaKey || event.ctrlKey) {
    return false
  }

  if (!/^\d$/.test(event.key)) {
    return true
  }

  const input = event.currentTarget as HTMLInputElement
  const selectionStart = input.selectionStart ?? input.value.length
  const selectionEnd = input.selectionEnd ?? input.value.length
  const selectedDigitCount = countDigits(input.value.slice(selectionStart, selectionEnd))
  const remainingDigitCount = countDigits(input.value) - selectedDigitCount

  return remainingDigitCount >= maxDigits
}

function getDriverLocation(): Promise<GeolocationCoordinates | null> {
  return new Promise((resolve) => {
    if (!navigator.geolocation) {
      resolve(null)
      return
    }

    navigator.geolocation.getCurrentPosition(
      (position) => resolve(position.coords),
      () => resolve(null),
      DRIVER_LOCATION_OPTIONS,
    )
  })
}

export async function getOptionalDriverLocation(): Promise<GeolocationCoordinates | null> {
  const permissionState = await getDriverLocationPermissionState()

  if (permissionState !== 'granted' && !hasGrantedDriverLocationPermission()) {
    return null
  }

  return getDriverLocation()
}

export async function getDriverLocationPermissionState(): Promise<PermissionState | 'unsupported'> {
  if (!navigator.permissions?.query) {
    return 'unsupported'
  }

  try {
    const permission = await navigator.permissions.query({ name: 'geolocation' as PermissionName })

    return permission.state
  } catch {
    return 'unsupported'
  }
}

export async function requestDriverLocationPermission(): Promise<boolean> {
  if (!navigator.geolocation) {
    return false
  }

  const permissionState = await getDriverLocationPermissionState()

  if (permissionState === 'denied') {
    return false
  }

  if (permissionState === 'granted') {
    return true
  }

  const location = await getDriverLocation()

  if (location) {
    setGrantedDriverLocationPermission()
  }

  return location !== null
}

function hasGrantedDriverLocationPermission(): boolean {
  try {
    return sessionStorage.getItem(DRIVER_LOCATION_PERMISSION_GRANTED_KEY) === '1'
  } catch {
    return false
  }
}

function setGrantedDriverLocationPermission() {
  try {
    sessionStorage.setItem(DRIVER_LOCATION_PERMISSION_GRANTED_KEY, '1')
  } catch {
    // Ignore storage failures. The browser permission result remains the source of truth.
  }
}

export function getBankTransferConfirmHtml(
  summary: WalletSummary,
  amount: number,
  currentLang: string,
): string {
  const amountText = getFormattedAmountHtml(amount, summary.currencySymbol)
  const accountHolderText = escapeHtml(getAccountHolderDisplayText(summary, currentLang))
  const accountText = escapeHtml(getAccountDisplayText(summary, currentLang))
  const amountLabel = i18n(currentLang, ['Amount', 'ຈຳນວນ'])
  const accountHolderLabel = i18n(currentLang, ['Name', 'ຊື່ບັນຊີ'])
  const accountLabel = i18n(currentLang, ['Account', 'ບັນຊີ'])

  return `
    <div style="text-align: left; line-height: 1.7; max-width: 100%;">
      <div><strong>${amountLabel}:</strong> ${amountText}</div>
      <div style="white-space: nowrap; overflow: hidden; text-overflow: ellipsis;"><strong>${accountHolderLabel}:</strong> ${accountHolderText}</div>
      <div><strong>${accountLabel}:</strong> ${accountText}</div>
    </div>
  `
}

export function getBankTransferSuccessHtml(
  result: WalletBankTransferResult,
  message: string,
): string {
  const reference = result.bankRefNo || result.xref

  return `
    <div style="line-height: 1.7;">
      <div style="font-size: 20px; color: #4B5563;">${escapeHtml(message)}</div>
      ${reference ? `<div style="font-size: 18px; color: #6B7280;">reference: ${escapeHtml(reference)}</div>` : ''}
    </div>
  `
}

export function escapeHtml(value: string): string {
  return value.replace(/[&<>"']/g, (character) => {
    const entities: Record<string, string> = {
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#039;',
    }

    return entities[character]
  })
}

function getAccountDisplayText(summary: WalletSummary, currentLang: string): string {
  return summary.accountNumber
    ? maskBankAccountNumber(summary.accountNumber)
    : i18n(currentLang, ['No bank account available', 'ບໍ່ມີບັນຊີທະນາຄານ'])
}

function getAccountHolderDisplayText(summary: WalletSummary, currentLang: string): string {
  return summary.accountHolderName || i18n(currentLang, ['No account holder available', 'ບໍ່ມີຊື່ບັນຊີ'])
}

function getFormattedAmountHtml(amount: number, symbol: string): string {
  return `<span><span style="font-size: 0.5em;">${escapeHtml(symbol)}</span>${escapeHtml(formatLakAmountValue(amount))}</span>`
}

function countDigits(value: string): number {
  return value.replace(/\D/g, '').length
}
